import { map, reduce } from "lodash";
import { Promise } from "es6-promise";
import { safeParseJSON } from "./utils/misc";

declare const jQuery: any;
const $ = jQuery;

export type FileMeta = { [metaKey: string]: any };

export interface FileUploadItem
{
    file: File;
    meta?: FileMeta;
}

export interface UploadProgressData
{
	fileIndex: number,
	totalFiles: number,
	file: File|undefined,
	fileMeta: FileMeta|undefined;
	fileProgress: number, // 0 <= n <= 1
	totalProgress: number, // 0 <= n <= 1
	totalBytesProgress: number,
	fileBytesProgress: number,
	totalBytes: number,
}

export interface UploadResult
{
	ok: boolean;
	serverData: any;
	files: FileUploadItem[];
	progress: UploadProgressData;
	statusCode: number;
	xhrEvent?: any;
}

export type UploadProgressHandler = (data: UploadProgressData) => void;

// uploadHandlerName - corresponds to NAME const on PHP FileUpload subclass
// Uploads one file at a time in sequence to avoid hitting server limits
// Returned Promise resolves immediately (no server request) if there are no files
// Resolves/rejects with UploadResult object
export function fileUpload(
	files: (File|FileUploadItem)[],
	uploadHandlerName: string,
	uploadData?: any,
	onProgress?: UploadProgressHandler): Promise<UploadResult>
{
	if (typeof onProgress !== "function")
	{
		onProgress = undefined;
	}

	const fileItems: FileUploadItem[] = normalizeFilesInputArray(files);
	const progressData: UploadProgressData = {
		totalFiles: files.length,
		fileIndex: 0,
		file: fileItems[0]?.file,
		fileMeta: fileItems[0]?.meta,
		fileBytesProgress: 0,
		fileProgress: 0, // float between 0 - 1
		totalProgress: 0, // float between 0 - 1
		totalBytesProgress: 0,
		totalBytes: reduce(fileItems, (acc, fileItem: FileUploadItem) => acc + fileItem.file.size, 0),
	};
	if (files.length === 0)
	{
		console.warn("fileUpload: No files to upload");
		return Promise.resolve({
			ok: true,
			serverData: null,
			files: fileItems,
			progress: progressData,
			statusCode: null,
			xhrEvent: null,
		});
	}
	return new Promise<UploadResult>((resolve, reject) => {
		const uploadDataJson: string = (uploadData === undefined) ? undefined : JSON.stringify(uploadData);
		const finalFileIndex: number = fileItems.length - 1;
		const onFileSuccess = (result: UploadResult): void => {
			if (progressData.fileIndex === finalFileIndex)
			{
				resolve(result);
				return;
			}
			++progressData.fileIndex;
			const nextFileItem: FileUploadItem = fileItems[progressData.fileIndex];
			progressData.file = nextFileItem.file;
			progressData.fileMeta = nextFileItem.meta;
			uploadFile();
		};
		const uploadFile = () => {
			if (!(progressData.file instanceof File))
			{
				throw new Error(`Failed to find file at index ${progressData.fileIndex}`);
			}
			const formData = new FormData();
			formData.append("iw-cfi-ul-handler", uploadHandlerName);
			formData.append("iw-cfi-file[]", progressData.file);
			if (progressData.fileMeta)
			{
				formData.append("iw-cfi-file-meta[]", JSON.stringify(progressData.fileMeta));
			}
			if (uploadDataJson)
			{
				formData.append("iw-cfi-files-meta", uploadDataJson);
			}
			const xhr = new XMLHttpRequest();
			xhr.upload.addEventListener("progress", function (e) {
				progressData.fileBytesProgress = e.loaded;
				progressData.fileProgress = e.loaded / e.total;
				progressData.totalBytesProgress += e.loaded;
				progressData.totalProgress = progressData.totalBytesProgress / progressData.totalBytes;
				if (onProgress)
				{
					onProgress(progressData);
				}
			});
			xhr.addEventListener("load", function (e) {
				const statusCode: number = e.target['status'];
				const serverData: any = safeParseJSON(xhr.responseText);
				if (statusCode < 400)
				{
					//FIXME - need to store EACH file server response and return them
					// at the promise reoslution - not just the final server response.
					onFileSuccess({
						ok: true,
						serverData,
						files: fileItems,
						progress: progressData,
						statusCode,
						xhrEvent: e,
					});
					return;
				}
				console.error("fileUpload xhr http error", xhr.status, xhr.statusText);
				xhr.abort();
				reject({
					ok: false,
					serverData,
					files,
					progress: progressData,
					statusCode,
					xhrEvent: e,
				});
			});
			xhr.addEventListener("error", e => {
				console.error("fileUpload xhr error", xhr.status, xhr.statusText);
				reject({
					ok: false,
					serverData: safeParseJSON(xhr.responseText),
					files,
					progress: progressData,
					statusCode: e.target['status'],
					xhrEvent: e,
				});
			});
			xhr.open("POST", "/"); // needs to be called after progress event listener add
			xhr.send(formData);
		};

		uploadFile();
	});
}

function normalizeFilesInputArray(files: (File|FileUploadItem)[]): FileUploadItem[]
{
	return map(files, (file: File|FileUploadItem): FileUploadItem => {
		if (file instanceof File)
		{
			return { file };
		}
		if (!(file.file instanceof File))
		{
			throw new Error(`Invalid array passed to normalizeFilesInputArray`);
		}
		return { file: file.file, meta: file.meta };
	});
}

if (!window["iw"])
{
	window["iw"] = {};
}

window["iw"].fileUpload = fileUpload;
